import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './DiagnosticAssessment.css';
import { Button, Card, CardContent, Typography, CircularProgress, Box } from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import axios from 'axios';
import { API_BASE_URL } from '../utils/api';

const DiagnosticAssessment: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState(false);
  const [readingLevel, setReadingLevel] = useState<number | null>(null);

  useEffect(() => {
    checkDiagnosticStatus();
  }, []);

  const checkDiagnosticStatus = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_BASE_URL}/api/student/diagnostic/status`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setCompleted(response.data.completed);
      setReadingLevel(response.data.reading_level);
    } catch (err: any) {
      console.error('Error checking diagnostic status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = () => {
    navigate('/student/test');
  };

  const handleNext = () => {
    navigate('/student/dashboard');
  };

  if (loading) {
    return (
      <div className="diagnostic-assessment-container">
        <Card className="diagnostic-card">
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <CircularProgress />
            </Box>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="diagnostic-assessment-container">
        <Card className="diagnostic-card">
          <CardContent>
            <Box className="diagnostic-icon">
              <AssessmentIcon sx={{ fontSize: 80, color: '#4caf50' }} />
            </Box>
            
            <Typography variant="h3" component="h1" gutterBottom align="center">
              {t('diagnosticAssessment.completed')}
            </Typography>
            
            {readingLevel && (
              <Typography variant="h5" color="primary" align="center" gutterBottom>
                {t('diagnosticAssessment.readingLevel')}: {readingLevel}
              </Typography>
            )}
            
            <Typography variant="body1" color="text.secondary" align="center" paragraph>
              {t('diagnosticAssessment.completedDescription')}
            </Typography>

            <Box className="action-box">
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={handleNext}
                fullWidth
              >
                {t('diagnosticAssessment.goToDashboard')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="diagnostic-assessment-container">
      <Card className="diagnostic-card">
        <CardContent>
          <Box className="diagnostic-icon">
            <AssessmentIcon sx={{ fontSize: 80, color: '#1976d2' }} />
          </Box>
          
          <Typography variant="h3" component="h1" gutterBottom align="center">
            {t('diagnosticAssessment.title')}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" align="center" paragraph>
            {t('diagnosticAssessment.description')}
          </Typography>

          <Box className="features-box">
            <Typography variant="h6" gutterBottom>
              {t('diagnosticAssessment.whatToExpect')}
            </Typography>
            <ul className="features-list">
              <li>📊 {t('diagnosticAssessment.features.readingLevel')}</li>
              <li>🎯 {t('diagnosticAssessment.features.skillAssessment')}</li>
              <li>📈 {t('diagnosticAssessment.features.progressTracking')}</li>
              <li>🎓 {t('diagnosticAssessment.features.personalizedLearning')}</li>
            </ul>
          </Box>

          <Box className="action-box">
            <Button
              variant="contained"
              size="large"
              startIcon={<PlayArrowIcon />}
              onClick={handleStartTest}
              fullWidth
              sx={{ marginBottom: 2 }}
            >
              {t('diagnosticAssessment.startTest')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              fullWidth
            >
              {t('diagnosticAssessment.skipForNow')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default DiagnosticAssessment;

